import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listDocumentTypes } from '../../api/workflow';
import { deleteLeadDocument, downloadLeadDocument } from '../../api/leads';
import { pushToast } from '../ui/toastBus';
import { saveBlobResponse } from '../../utils/download';
import LeadDocumentUploadModal from './LeadDocumentUploadModal';

const STATE_STYLES = {
  missing: 'bg-gray-100 text-gray-600',
  uploaded: 'bg-amber-100 text-amber-800',
};

export default function LeadDocumentsPanel({ leadId, requiredCodes, documents, editable }) {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes });
  const [uploadType, setUploadType] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: deleteLeadDocument,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Document deleted.' });
      queryClient.invalidateQueries({ queryKey: ['leads', String(leadId)] });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete document.' }),
  });

  const handleDownload = async (doc) => {
    try {
      const response = await downloadLeadDocument(doc.id);
      saveBlobResponse(response, doc.original_name);
    } catch {
      pushToast({ type: 'error', message: 'Could not download document.' });
    }
  };

  const nameFor = (code) => typesQuery.data?.find((t) => t.code === code)?.name || code;

  if (!requiredCodes || requiredCodes.length === 0) {
    return <p className="text-sm text-gray-400">This category has no required documents at registration.</p>;
  }

  return (
    <div>
      <div className="space-y-1.5">
        {requiredCodes.map((code) => {
          const doc = documents.find((d) => d.document_type === code);
          const state = doc ? 'uploaded' : 'missing';

          return (
            <div key={code} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2">
              <span className="text-sm text-gray-700">{nameFor(code)}</span>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATE_STYLES[state]}`}>
                  {state}
                </span>
                {doc && (
                  <>
                    <button onClick={() => handleDownload(doc)} className="text-xs font-medium text-primary hover:underline">
                      Download
                    </button>
                    {editable && (
                      <button
                        onClick={() => deleteMutation.mutate(doc.id)}
                        disabled={deleteMutation.isPending}
                        className="text-xs font-medium text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
                {!doc && editable && (
                  <button onClick={() => setUploadType(code)} className="text-xs font-medium text-primary hover:underline">
                    Upload
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editable && (
        <LeadDocumentUploadModal open={!!uploadType} onClose={() => setUploadType(null)} leadId={leadId} presetTypeCode={uploadType} />
      )}
    </div>
  );
}
